import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import MaterialTextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import { useFormContext } from '../Form';
import clsx from 'clsx';
import styled from 'styled-components';
import ErrorMessage from '../ErrorMessage';
import Icon from '../Icon';
import useRegisterInput from '../Form/useRegisterInput';

const StyledTextField = styled(MaterialTextField)`
  && {
    &.inline {
      &:hover {
        .inlineEditField-adornment {
          visibility: visible !important;
          opacity: 1;
        }
      }

      .MuiOutlinedInput-notchedOutline {
        transition: ${props =>
          props.theme.transitions.create('border-color', {
            easing: props.theme.transitions.easing.sharp,
            duration: props.theme.transitions.duration.shortest,
          })};
      }

      .MuiInputBase-root.Mui-disabled {
        color: ${props => props.theme.palette.text.primary};

        .MuiOutlinedInput-notchedOutline {
          border-color: transparent;
        }
      }
    }
  }
`;

const HiddenIconButton = styled(IconButton)`
  && {
    visibility: hidden;
    opacity: 0;
    transition: ${props =>
      props.theme.transitions.create('opacity', {
        easing: props.theme.transitions.easing.sharp,
        duration: props.theme.transitions.duration.shortest,
      })};
  }
`;

const TextField = props => {
  const formContext = useFormContext();
  const {
    editInline: formInline = false,
    isEditing: formIsEditing = true,
    errors = {},
    setIsEditing,
    setValue,
    watch,
  } = formContext || {};
  const {
    name,
    inline = formInline,
    isEditing = formIsEditing,
    error = !!errors[name],
    errorMessage,
    onEdit,
    onChange,
    readOnly,
    helperText,
    value,
    defaultValue,
    register: shouldRegister,
    className,
    ...inputProps
  } = props;

  useRegisterInput(name, shouldRegister);

  const isInline = inline && !isEditing;
  const showEditAdornment = isInline && !readOnly;
  const inputValue = value ?? (watch && watch(name));
  const defaultValueRef = useRef(defaultValue);

  useEffect(() => {
    defaultValueRef.current = defaultValue;
  });

  /**
   * On edit handler.
   */
  const handleOnEdit = () => {
    if (formContext && !onEdit) {
      setIsEditing(true);
    }

    if (onEdit) {
      onEdit();
    }
  };

  /**
   * Handle the change on the select.
   * @param {object|Event} ev
   */
  const handleOnChange = ev => {
    if (formContext && !onChange) {
      setValue(name, ev.target.value);
      return;
    }

    if (onChange) {
      onChange(ev, ev.target.value);
    }
  };

  /**
   * Return the error message.
   */
  const getErrorMessage = () => {
    if (formContext && !errorMessage) {
      return errors[name] && <ErrorMessage errors={errors} name={name} />;
    }

    return errorMessage;
  };

  if (inline) {
    inputProps.InputLabelProps = {
      ...(inputProps.InputLabelProps || {}),
      shrink: true,
    };
  }

  if (readOnly) {
    inputProps.disabled = true;
  }

  if (showEditAdornment) {
    inputProps.InputProps = {
      ...(inputProps.InputProps || {}),
      endAdornment: (
        <HiddenIconButton className="inlineEditField-adornment" onClick={handleOnEdit} edge="end">
          <Icon icon={['fal', 'pencil']} fontSize="small" />
        </HiddenIconButton>
      ),
    };
    inputProps.disabled = true;
  }

  return (
    <StyledTextField
      {...inputProps}
      name={name}
      className={clsx({ inline: isInline || (inline && readOnly) }, className)}
      error={error}
      helperText={error ? getErrorMessage() : helperText}
      value={inputValue ?? (readOnly ? '-' : inputValue)}
      onChange={handleOnChange}
    />
  );
};

TextField.propTypes = {
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
  variant: PropTypes.string,
  margin: PropTypes.string,
  fullWidth: PropTypes.bool,
  inline: PropTypes.bool,
  isEditing: PropTypes.bool,
  onChange: PropTypes.func,
  onEdit: PropTypes.func,
  readOnly: PropTypes.bool,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  helperText: PropTypes.string,
  value: PropTypes.any,
  register: PropTypes.bool,
  defaultValue: PropTypes.any,
};

TextField.defaultProps = {
  variant: 'outlined',
  className: null,
  margin: 'normal',
  value: undefined,
  fullWidth: true,
  inline: undefined,
  isEditing: undefined,
  onChange: undefined,
  onEdit: undefined,
  readOnly: false,
  inputRef: undefined,
  error: undefined,
  errorMessage: undefined,
  helperText: undefined,
  register: true,
  defaultValue: undefined,
};

export default TextField;
